import {
  BufferAttribute,
  BufferGeometry,
  Group,
  Mesh,
  Object3D,
  Vector3,
} from 'three'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { convertPosition } from '../core/unity'
import type { AssetLibrary } from './assetLibrary'

/**
 * Sweep `Road.obj` along the spline, the way Dreamteck's SplineMesh does.
 *
 * `Road.obj` is a tile: 6.5 wide, 4 long, 0.03–0.33 tall, with the kerb, the
 * driving surface and the strip that blends into the ground all carried as
 * separate bands of the vertex-colour mask. Repeating it as rigid instances
 * would gap and overlap on corners, so instead every vertex is bent onto the
 * curve — its distance along the tile becomes distance along the road, and its
 * cross-section offset is applied in the curve's local frame.
 *
 * The shipped scene has the result of this already, as `SplineMesh (1)`'s baked
 * `Custom Mesh` — but a baked mesh is one of the two things AssetRipper could
 * not export, so it is swept again from the tile it was baked from. Its two
 * materials are the record of what the two Dreamteck channels were:
 * `RoadMain` for the road and `Ground 1` for the centre-line dashes.
 *
 * The road is clipped to `originalStart/EndPercent`, so it stops where the
 * level author drew it and the entry/exit extensions stay bare.
 */
export interface SplineRoad {
  object: Object3D
  dispose(): void
}

export interface SplineRoadTuning {
  dashSpacing: number
  dashHeight: number
}

const UP = new Vector3(0, 1, 0)

/**
 * What to draw the swept tile with.
 *
 * `SplineMesh (1)`'s baked mesh names `RoadMain` and `Ground 1`, and those are
 * the two Dreamteck channels, so those are what the loop uses. `RoadMain` is
 * also where the road's colour lives: its palette is navy for the R band, white
 * for the G kerb and a pale blue for the B skirt, which is exactly the road in
 * the shipped game. The static stubs in `JamArea` use `Road` instead, whose
 * palette is much lighter — they are genuinely different materials, not a
 * mistake, and swapping the loop onto `Road` turns the whole ring pale.
 *
 * `RoadMain`'s texture is banded across the sweep, so the UVs have to be
 * generated the way Dreamteck generates them — V across the cross-section, U
 * along the spline. Feeding it the tile's own UVs, which run the other way,
 * lays those bands *along* the road and stripes it every tile.
 */
const ROAD_MATERIAL = 'RoadMain'
const DASH_MATERIAL = 'Ground 1'

/**
 * How much road one swept segment covers, in world units.
 *
 * `Road.obj` has exactly two rings of vertices — one at each end — so a copy of
 * it is a straight prism no matter how the spline curves inside it. Sweeping
 * one copy per tile length (4 units) turns the loop into a thirty-sided
 * polygon with visible flats on every corner. The cross-section is constant
 * along the tile, so a copy can be squashed to any length without distorting
 * it; this is the segment length the sweep actually steps by.
 */
const ROAD_SEGMENT = 0.5

export function buildSplineRoad(
  library: AssetLibrary,
  road: RoadSpline,
  tuning: SplineRoadTuning,
): SplineRoad | null {
  const tile = library.peekMesh('Road')
  if (!tile) return null

  const { spline, originalStartPercent, originalEndPercent } = road
  const startDistance = spline.distanceAt(originalStartPercent)
  const roadLength = spline.distanceAt(originalEndPercent) - startDistance
  if (roadLength < 1e-3) return null

  const group = new Group()
  group.name = 'road'
  const owned: BufferGeometry[] = []

  const tileCount = Math.max(1, Math.round(roadLength / ROAD_SEGMENT))
  // Stretch slightly so a whole number of segments spans the road exactly.
  const surface = bend(tile, spline, {
    startDistance,
    step: roadLength / tileCount,
    span: roadLength / tileCount,
    count: tileCount,
    yOffset: 0,
    // `RoadMain` wants Dreamteck's UVs, not the tile's; see ROAD_MATERIAL.
    sweepUv: true,
  })
  if (surface) {
    // The mask stays raw: the shader resolves it against `RoadMain`'s palette,
    // which is also what lets the base map's bands land across the road.
    const mesh = new Mesh(surface, library.material(ROAD_MATERIAL))
    mesh.name = 'roadSurface'
    mesh.receiveShadow = true
    group.add(mesh)
    owned.push(surface)
  }

  const dashTile = library.peekMesh('RoadLine')
  if (dashTile) {
    const dashLength = lengthOf(dashTile)
    const dashCount = Math.max(1, Math.round(roadLength / tuning.dashSpacing))
    const spacing = roadLength / dashCount
    const dashes = bend(dashTile, spline, {
      // Centre each dash in its slot rather than butting it against the start.
      startDistance: startDistance + (spacing - dashLength) / 2,
      step: spacing,
      span: dashLength,
      count: dashCount,
      yOffset: tuning.dashHeight,
    })
    if (dashes) {
      // `Ground 1` is a plain colour with no palette, and the dash mesh carries
      // no mask, so it draws as the flat near-white the baked road had.
      const mesh = new Mesh(dashes, library.material(DASH_MATERIAL))
      mesh.name = 'roadDashes'
      group.add(mesh)
      owned.push(dashes)
    }
  }

  if (group.children.length === 0) return null

  return {
    object: group,
    dispose() {
      for (const g of owned) g.dispose()
    },
  }
}

/** How long a tile is along its sweep axis — local −Z, once converted. */
function lengthOf(tile: BufferGeometry): number {
  if (!tile.boundingBox) tile.computeBoundingBox()
  const box = tile.boundingBox!
  return box.max.z - box.min.z
}

interface BendOptions {
  /** Distance along the spline the first copy starts at. */
  startDistance: number
  /** Distance between copies. */
  step: number
  /** How much road one copy covers — equal to `step` when tiling seamlessly. */
  span: number
  count: number
  yOffset: number
  /**
   * Replace the tile's UVs with the ones Dreamteck generates as it sweeps: V
   * across the cross-section, U along the spline in world units.
   */
  sweepUv?: boolean
}

/**
 * Lay `count` copies of a tile onto the curve, bending each one as it goes.
 *
 * Every attribute the tile carries is copied through verbatim except position
 * and normal, which are rebuilt in the curve's frame. That keeps the UVs and,
 * crucially, the vertex-colour mask — the mask is the only thing that says
 * which band of the cross-section is kerb, road or ground skirt.
 */
function bend(
  tile: BufferGeometry,
  spline: RoadSpline['spline'],
  options: BendOptions,
): BufferGeometry | null {
  const position = tile.getAttribute('position')
  const normal = tile.getAttribute('normal')
  if (!position) return null

  if (!tile.boundingBox) tile.computeBoundingBox()
  const zMax = tile.boundingBox!.max.z
  const tileLength = lengthOf(tile)
  if (tileLength < 1e-6) return null

  const per = position.count
  const total = per * options.count
  const positions = new Float32Array(total * 3)
  const normals = normal ? new Float32Array(total * 3) : null
  const sweepUvs = options.sweepUv ? new Float32Array(total * 2) : null
  const xMin = tile.boundingBox!.min.x
  const xSpan = Math.max(tile.boundingBox!.max.x - xMin, 1e-6)

  const curvePos = new Vector3()
  const tangent = new Vector3()
  const right = new Vector3()

  let write = 0
  for (let copy = 0; copy < options.count; copy++) {
    const origin = options.startDistance + copy * options.step

    for (let v = 0; v < per; v++) {
      const lx = position.getX(v)
      const ly = position.getY(v)
      const lz = position.getZ(v)

      // How far into the tile this vertex sits, 0 at the near edge.
      const along = (zMax - lz) / tileLength
      const distance = origin + along * options.span
      const percent = spline.percentAtDistance(distance)
      spline.evaluatePosition(percent, curvePos)
      spline.evaluateTangent(percent, tangent)
      convertPosition(curvePos, curvePos)
      convertPosition(tangent, tangent)
      tangent.y = 0
      if (tangent.lengthSq() < 1e-12) tangent.set(0, 0, -1)
      tangent.normalize()
      right.crossVectors(tangent, UP)

      const o = write * 3
      positions[o] = curvePos.x + right.x * lx
      positions[o + 1] = curvePos.y + ly + options.yOffset
      positions[o + 2] = curvePos.z + right.z * lx

      if (normals && normal) {
        // Rotate the tile normal into the curve frame: local X → right,
        // local Y → up, local −Z → tangent.
        const nx = normal.getX(v)
        const ny = normal.getY(v)
        const nz = normal.getZ(v)
        normals[o] = right.x * nx + tangent.x * -nz
        normals[o + 1] = ny
        normals[o + 2] = right.z * nx + tangent.z * -nz
      }

      if (sweepUvs) {
        // U runs with the road so the map tiles along it; V spans the
        // cross-section so the base map's bands sit on the kerbs, which is
        // where they were painted to go.
        sweepUvs[write * 2] = distance / tileLength
        sweepUvs[write * 2 + 1] = (lx - xMin) / xSpan
      }

      write++
    }
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  if (normals) geometry.setAttribute('normal', new BufferAttribute(normals, 3))

  // Everything else — UVs and the vertex-colour mask — repeats unchanged.
  for (const name of Object.keys(tile.attributes)) {
    if (name === 'position' || name === 'normal') continue
    if (name === 'uv' && sweepUvs) continue
    const source = tile.getAttribute(name)
    const size = source.itemSize
    const array = new Float32Array(total * size)
    for (let copy = 0; copy < options.count; copy++) {
      for (let v = 0; v < per; v++) {
        const o = (copy * per + v) * size
        for (let k = 0; k < size; k++) {
          array[o + k] = (source.array as ArrayLike<number>)[v * size + k]
        }
      }
    }
    geometry.setAttribute(name, new BufferAttribute(array, size))
  }
  if (sweepUvs) geometry.setAttribute('uv', new BufferAttribute(sweepUvs, 2))


  const index = tile.getIndex()
  if (index) {
    const indices = new Uint32Array(index.count * options.count)
    for (let copy = 0; copy < options.count; copy++) {
      const offset = copy * per
      for (let i = 0; i < index.count; i++) {
        indices[copy * index.count + i] = index.getX(i) + offset
      }
    }
    geometry.setIndex(new BufferAttribute(indices, 1))
  }

  if (!normals) geometry.computeVertexNormals()
  geometry.computeBoundingSphere()
  return geometry
}
